import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { createFileRoute, redirect } from '@tanstack/react-router'
import type { Feature, Polygon } from 'geojson'
import type { LngLatBoundsLike } from 'mapbox-gl'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { Layer, Marker, Source } from 'react-map-gl/mapbox'
import { z } from 'zod'
import { CustomRadioGroup } from '../../components/CustomRadioGroup.tsx'
import { FormLayout2 } from '../../components/FormLayout2.tsx'
import { Alert } from '../../components/generic.tsx'
import { I18n } from '../../components/I18n.tsx'
import { Icon } from '../../components/Icon.tsx'
import { FormLayout, FormPage } from '../../components/Layouts.tsx'
import { SearchField } from '../../components/SearchField.tsx'
import { TitleSubtitle } from '../../components/TitleSubtitle.tsx'
import { assetQuery } from '../../data/assets.ts'
import { deviceListQuery } from '../../data/devices.ts'
import { type Location, useLocations } from '../../data/locations.tsx'
import { useStartWindRun, useWindLocation, windLocationId } from '../../data/windDownscaler.ts'
import { FormMap } from '../../features/alerts/components/FormMap.tsx'
import { useSearchLocations } from '../../features/locations/locationUtils.ts'
import { SliderField } from '../../features/windDownscaler/SliderField.tsx'
import { usualDuration } from '../../features/windDownscaler/windDuration.ts'
import { DECISION_THRESHOLD_RANGE, TURBULENCE_ADVISORY_THRESHOLD_M_S } from '../../features/windDownscaler/windThreshold.ts'
import { flagVariation } from '../../launchdarkly.ts'
import { useNavigateBack } from '../../navigation.ts'
import { useMap } from '../../states.ts'

export const Route = createFileRoute('/_app/wind/add')({
	validateSearch: z.object({ locationId: z.string().optional() }),
	beforeLoad: ({ context: { isAdmin, ldClient } }) => {
		if (!isAdmin || !ldClient || !flagVariation(ldClient, 'windDownscalerPrototype')) {
			throw redirect({ to: '/' })
		}
	},
	loader: async ({ context: { queryClient, impersonation, sub } }) => {
		const hasProtectionsSub = sub.features.includes('protections')
		await Promise.all([
			queryClient.query({ ...assetQuery(impersonation, { enabled: hasProtectionsSub }), staleTime: 'static' }),
			queryClient.query({ ...deviceListQuery(impersonation?.partyAccountId), staleTime: 'static' }),
		])
	},
	component: CreateWind,
})

const SIDE_LENGTH_RANGE = { min: 1000, max: 5000, step: 100, markStep: 1000 }
const DEFAULT_SIDE_LENGTH_M = 3000
const METRES_PER_DEGREE = 111_320
const AREA_PADDING_PX = 80

const STEPS = [
	{ id: 'location', label: 'Location', description: 'Where to compute 3D wind' },
	{ id: 'area', label: 'Area and decision threshold', description: 'Square around the location and the alert level' },
]

function CreateWind() {
	const { locationId } = Route.useSearch()
	const navigate = Route.useNavigate()
	const navigateBack = useNavigateBack()
	const { data: locations } = useLocations()
	const location = locations.find((item) => item.id === locationId)
	const [activeStep, setActiveStep] = useState(0)
	const [sideLengthM, setSideLengthM] = useState(DEFAULT_SIDE_LENGTH_M)
	const [threshold, setThreshold] = useState(TURBULENCE_ADVISORY_THRESHOLD_M_S)
	const startRun = useStartWindRun(location ? windLocationId(location) : '')
	const { data: windLocation, isError } = useWindLocation(location && windLocationId(location))
	const canStart = windLocation?.running === false && !windLocation.available
	const notice = windLocation?.running
		? '3D wind processing is in progress for this location.'
		: windLocation?.available
			? 'This location already has 3D wind.'
			: isError
				? '3D wind cannot be created for this location.'
				: undefined

	const close = () => navigateBack({ fallback: '/' })
	const save = () => {
		if (!location) return
		startRun.mutate(
			{ sideLengthM, decisionThresholdMS: threshold },
			{
				onSuccess: () => {
					enqueueSnackbar(`3D wind processing started. ${usualDuration(sideLengthM)}`, { variant: 'success' })
					return navigate({ to: '/', search: { locationId: location.id, panel: location.type === 'asset' ? 'alerts' : 'charts' } })
				},
				onError: () => enqueueSnackbar('The 3D wind processing did not start. Try again.', { variant: 'error' }),
			},
		)
	}

	return (
		<FormLayout2
			onBack={close}
			header={<I18n i18nKey={($) => $.windDownscaler.create} />}
			sideBar={<FormLayout2.Steps steps={STEPS} activeStep={activeStep} />}
			bottomBar={
				<>
					<FormLayout2.Buttons
						onStepChange={setActiveStep}
						activeStep={activeStep}
						stepCount={STEPS.length}
						onCancel={close}
						onNext={() => canStart}
					/>
					{activeStep === STEPS.length - 1 && (
						<Button onClick={save} disabled={startRun.isPending || !canStart}>
							Confirm & process 3D wind
							{startRun.isPending && <CircularProgress sx={{ ml: 1 }} />}
						</Button>
					)}
				</>
			}
		>
			<FormLayout.Split
				left={
					<FormPage>
						{activeStep === 1 && location ? (
							<AreaStep
								location={location}
								sideLengthM={sideLengthM}
								onSideLengthChange={setSideLengthM}
								threshold={threshold}
								onThresholdChange={setThreshold}
							/>
						) : (
							<LocationStep
								locations={locations}
								locationId={location?.id}
								notice={notice}
								onLocationChange={(id) => navigate({ search: { locationId: id }, replace: true })}
							/>
						)}
					</FormPage>
				}
				right={
					<FormMap selectedLocation={location} interactive={activeStep === 0}>
						{activeStep === 1 && location && <AreaSquare location={location} sideLengthM={sideLengthM} />}
					</FormMap>
				}
			/>
		</FormLayout2>
	)
}

const areaSquare = ({ lat, lon }: Location, sideLengthM: number) => {
	const halfLat = sideLengthM / 2 / METRES_PER_DEGREE
	const halfLon = halfLat / Math.cos((lat * Math.PI) / 180)
	const [west, east, south, north] = [lon - halfLon, lon + halfLon, lat - halfLat, lat + halfLat]
	const square: Feature<Polygon> = {
		type: 'Feature',
		properties: {},
		geometry: {
			type: 'Polygon',
			coordinates: [
				[
					[west, south],
					[east, south],
					[east, north],
					[west, north],
					[west, south],
				],
			],
		},
	}
	const bounds: LngLatBoundsLike = [
		[west, south],
		[east, north],
	]
	return { square, bounds, north }
}

const AreaSquare = ({ location, sideLengthM }: { location: Location; sideLengthM: number }) => {
	const map = useMap((s) => s.map)
	const theme = useTheme()
	const { square, north } = useMemo(() => areaSquare(location, sideLengthM), [location, sideLengthM])
	const largestBounds = useMemo(() => areaSquare(location, SIDE_LENGTH_RANGE.max).bounds, [location])

	useEffect(() => {
		map?.fitBounds(largestBounds, { padding: AREA_PADDING_PX })
	}, [map, largestBounds])

	return (
		<>
			<Source id='wind-area' type='geojson' data={square}>
				<Layer id='wind-area-fill' type='fill' paint={{ 'fill-color': theme.palette.primary.main, 'fill-opacity': 0.15 }} />
				<Layer id='wind-area-line' type='line' paint={{ 'line-color': theme.palette.primary.main, 'line-width': 2 }} />
			</Source>
			<Marker longitude={location.lon} latitude={north} anchor='bottom'>
				<Paper sx={{ px: 0.75, py: 0.25, mb: 0.5 }}>
					<Typography variant='code2'>{sideLengthM} m</Typography>
				</Paper>
			</Marker>
		</>
	)
}

type LocationStepProps = {
	locations: Location[]
	locationId: string | undefined
	notice: string | undefined
	onLocationChange: (locationId: string) => void
}

const LocationStep = ({ locations, locationId, notice, onLocationChange }: LocationStepProps) => {
	const [search, setSearch] = useState('')
	const found = useSearchLocations(locations, search)
	return (
		<FormPage.Content>
			<TitleSubtitle title='Location' subtitle='Select the asset or device to compute 3D wind for.' />
			{notice && (
				<Alert icon={<Icon name='CircleInfoSolid' />} severity='info'>
					{notice}
				</Alert>
			)}
			<SearchField autoFocus value={search} onChange={setSearch} />
			<Box sx={{ flexGrow: 1, minH: 0, overflowY: 'auto' }}>
				<CustomRadioGroup
					options={found.map((item) => ({ value: item.id, label: item.name, icon: item.icon }))}
					value={locationId}
					onChange={onLocationChange}
				/>
			</Box>
		</FormPage.Content>
	)
}

type AreaStepProps = {
	location: Location
	sideLengthM: number
	onSideLengthChange: (sideLengthM: number) => void
	threshold: number
	onThresholdChange: (threshold: number) => void
}

const AreaStep = ({ location, sideLengthM, onSideLengthChange, threshold, onThresholdChange }: AreaStepProps) => (
	<FormPage.Content>
		<TitleSubtitle
			title='Area and decision threshold'
			subtitle={`3D wind is computed inside a square centred on ${location.name}. Wind speeds above the threshold are shown in red on the map.`}
		/>
		<SliderField
			label='Side length (m)'
			{...SIDE_LENGTH_RANGE}
			value={sideLengthM}
			onChange={(value) => onSideLengthChange(Math.round(value / SIDE_LENGTH_RANGE.step) * SIDE_LENGTH_RANGE.step)}
		/>
		<Stack sx={{ justify: 'space-between', align: 'center', px: 1.5, py: 1, radius: 1, bgcolor: (t) => t.palette.background.paper1 }}>
			<Typography variant='label'>Area</Typography>
			<Typography variant='code1'>
				{sideLengthM} × {sideLengthM} m · {((sideLengthM / 1000) ** 2).toFixed(2)} km²
			</Typography>
		</Stack>
		<Stack col spacing={0.5}>
			<SliderField label='Decision threshold (m/s)' {...DECISION_THRESHOLD_RANGE} value={threshold} onChange={onThresholdChange} />
			<Typography variant='label' color='textSecondary'>
				The 3D wind view starts with this threshold. You can change it there at any time.
			</Typography>
		</Stack>
	</FormPage.Content>
)
